/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';

import { getApi, putApi, postApi } from 'lib/axios';
import { BASE_URL } from 'utils/const';

import { enqueueSnackbar } from './notificationSlice';

/**
 * 注文情報を全件取得する
 */
export const fetchAllOrders = createAsyncThunk(
  'orders/fetchAllOrders',
  async (data, { dispatch }) => {
    const param = {
      per_page: data.rowsPerPage,
      page: data.page,
      order_id: data.orderId,
      user_name: data.userName,
      created_at_from: data.created_at_from,
      created_at_to: data.created_at_to,
      simplified_car_model_text: data.simplifiedCarModelText,
      chassis_number_text: data.chassisNumberText,
      shipping_digest_codes: data.shippingDigestCodes,
    };

    try {
      const res = await getApi(
        `${BASE_URL}/orders`,
        // ここに「params」というキー名でセットし、クエリパラメータを指定する
        param,
      );

      return {
        data: res.data,
        total: res.meta.total,
        adminList: res.meta.admin_list,
        shippingDigestCode: res.meta.shipping_digest_list,
      }; // action.payloadとしてReducerで読み取れる
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 注文対応状況を更新する
 */
/**
 * 注文明細CSVをダウンロードする
 */
export const fetchOrdersCsv = createAsyncThunk(
  'orders/fetchOrdersCsv',
  async (data, { dispatch }) => {
    const param = {
      user_name: data.userName,
      created_at_from: data.created_at_from,
      created_at_to: data.created_at_to,
      simplified_car_model_text: data.simplifiedCarModelText,
      chassis_number_text: data.chassisNumberText,
      shipping_digest_codes: data.shippingDigestCodes,
    };

    try {
      const res = await getApi(`${BASE_URL}/orders/csv`, param);

      return { data: res.data || res };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'CSVのダウンロードに失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * 注文の月締め集計を取得する
 */
export const fetchOrdersSummary = createAsyncThunk(
  'orders/fetchOrdersSummary',
  async (data, { dispatch }) => {
    const param = {
      user_name: data.userName,
      created_at_from: data.created_at_from,
      created_at_to: data.created_at_to,
      simplified_car_model_text: data.simplifiedCarModelText,
      chassis_number_text: data.chassisNumberText,
    };

    try {
      const res = await getApi(`${BASE_URL}/orders/summary`, param);

      return res.data;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: '月別集計の取得に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

export const updateOrderHandleStatus = createAsyncThunk(
  'orders/updateOrderHandleStatus',
  async (param) => {
    const params = {
      payment_status_code: param.paymentStatusCode,
      admin_id: param.adminId,
    };

    const res = await putApi(`${BASE_URL}/orders/${param.orderId}`, params);

    return {
      id: param.orderId,
      order: res.data,
    };
  },
);

/**
 * 注文情報を更新する
 */
export const updateOrderItem = createAsyncThunk(
  'orders/updateOrderItem',
  async (param, { dispatch }) => {
    const params = {
      order_id: param.orderId,
      shipping_status_code: param.shippingStatusCode,
      shipping_number: param.shippingNumber,
      // 未選択は null で送る。キーごと落とすと api は今の配送業者を残す
      deliverer_id: param.delivererId ?? null,
      shipping_company_name: param.shippingCompanyName,
    };

    try {
      const res = await putApi(`${BASE_URL}/orders/order_item/${param.orderItemId}`, params);

      dispatch(
        enqueueSnackbar({
          message: '発送情報を更新しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id: param.orderId,
        order: res.data.data,
      };
    } catch (error) {
      // 403（外部管理者の後退・取引キャンセル等）や422を無言で握り潰すと
      // 「保存できた」と誤認するため、サーバのメッセージをそのまま表示する
      dispatch(
        enqueueSnackbar({
          message: error?.response?.data?.message ?? 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 保存に失敗したときに出す文言を返す
 *
 * 422 はサーバが message を定型文に置き換え、理由は errors（{項目名: [文言, ...]}）にだけ入る。
 * 403 などは errors に例外の英文が文字列で入るので使わない。
 */
const saveErrorMessage = (error) => {
  const data = error?.response?.data;
  const firstError =
    data?.errors && typeof data.errors === 'object'
      ? Object.values(data.errors).flat()[0]
      : undefined;

  return firstError ?? data?.message ?? 'エラーが発生しました。';
};

/**
 * 注文内容（お客様が入力した項目と明細の単価・数量）を変更する
 *
 * 単価・数量や都道府県を変えると合計も変わるため、呼び出し側で注文を取り直すこと。
 */
export const updateOrderContents = createAsyncThunk(
  'orders/updateOrderContents',
  async (param, { dispatch }) => {
    try {
      const res = await putApi(`${BASE_URL}/orders/${param.orderId}/contents`, param.contents);

      dispatch(
        enqueueSnackbar({
          message: '注文内容を更新しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id: param.orderId,
        order: res.data.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: saveErrorMessage(error),
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 注文商品をATRS(JARA)へ注文する
 *
 * JARAへの同期リクエストを待つため、呼び出し側でボタンの二度押しを抑止すること。
 */
export const jaraOrderOrderItem = createAsyncThunk(
  'orders/jaraOrderOrderItem',
  async (param, { dispatch }) => {
    try {
      // 保存操作を挟まずに注文しても、画面に出ている内容がそのまま送られるようにする
      const res = await postApi(`${BASE_URL}/orders/order_item/${param.orderItemId}/jara_order`, {
        atrs_order_comment: param.atrsOrderComment,
      });

      // JARAが発注を拒否した場合もHTTP 200で返るため、orderedで成否を判定する
      dispatch(
        enqueueSnackbar({
          message: res.ordered
            ? 'ATRSへ注文しました。'
            : `ATRSへの注文に失敗しました。${res.order_failure_reason ?? ''}`,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: res.ordered ? 'success' : 'error',
          },
        }),
      );

      return res;
    } catch (error) {
      // 403（外部管理者）・409（二重発注）・422（対象外部品）はサーバのメッセージをそのまま出す
      dispatch(
        enqueueSnackbar({
          message: error?.response?.data?.message ?? 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * ATRS発注コメントを保存する
 *
 * ATRSには着日を指定する項目が無く、このコメントで在庫業者へお届け予定日を伝えている。
 */
export const updateAtrsOrderComment = createAsyncThunk(
  'orders/updateAtrsOrderComment',
  async (param, { dispatch }) => {
    try {
      const res = await putApi(
        `${BASE_URL}/orders/order_item/${param.orderItemId}/atrs_order_comment`,
        { atrs_order_comment: param.atrsOrderComment },
      );

      dispatch(
        enqueueSnackbar({
          message: 'ATRS発注コメントを保存しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: error?.response?.data?.message ?? 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * メモを登録する
 */
export const createAdminMemo = createAsyncThunk(
  'users/createMemo',
  async ({ orderId: id, memo }, { dispatch }) => {
    try {
      const params = { admin_id: 1, admin_memo: memo };
      const res = await postApi(`${BASE_URL}/orders/${id}`, params);

      dispatch(
        enqueueSnackbar({
          message: 'コメントを追加しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id,
        user: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

const ordersAdapter = createEntityAdapter({
  selectId: (e) => e.order_id,
});

export const orderSlice = createSlice({
  name: 'orders',
  initialState: ordersAdapter.getInitialState({
    loading: true,
  }),
  reducers: {
    addTag: ordersAdapter.addOne,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllOrders.fulfilled, (state, action) => {
      ordersAdapter.setAll(state, action.payload.data);
      state.loading = false;
    });
    builder.addCase(fetchAllOrders.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAllOrders.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createAdminMemo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createAdminMemo.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createAdminMemo.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateOrderHandleStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateOrderHandleStatus.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateOrderHandleStatus.fulfilled, (state, action) => {
      ordersAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { ...action.payload.order },
      });
      state.loading = false;
    });
    builder.addCase(updateOrderItem.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateOrderItem.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateOrderItem.fulfilled, (state, action) => {
      ordersAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { ...action.payload.order },
      });
      state.loading = false;
    });
  },
});

export const orderSelectors = ordersAdapter.getSelectors((state) => state.orders);

export default orderSlice.reducer;
